/* eslint-disable jsx-a11y/no-autofocus */
import { FC, useState } from 'react'

import { Button } from 'components/atoms/Button'
import { dateToReadableString } from 'utils'

export interface EmailAlertRecipient {
  id: string
  recipientEmail: string
  isActive: boolean
  createdBy?: string
  modifiedBy?: string
  updatedOn?: string
}

const GRID_COLS = 'grid-cols-[minmax(220px,1fr)_170px_150px_96px]'

export interface RecipientRowProps {
  recipient: EmailAlertRecipient
  canEdit: boolean
  isEditing: boolean
  isConfirmingDelete: boolean
  editError: string
  onStartEdit: () => void
  onCancelEdit: () => void
  onSaveEdit: (newEmail: string) => void
  onStartDelete: () => void
  onCancelDelete: () => void
  onConfirmDelete: () => void
}

export const RecipientRow: FC<RecipientRowProps> = ({
  recipient,
  canEdit,
  isEditing,
  isConfirmingDelete,
  editError,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onStartDelete,
  onCancelDelete,
  onConfirmDelete,
}: RecipientRowProps) => {
  const [editValue, setEditValue] = useState(recipient.recipientEmail)

  if (isEditing) {
    return (
      <div className='flex items-center gap-2 flex-wrap px-3 py-[9px] border-b border-N-100 bg-P-50 rounded-md'>
        <input
          value={editValue}
          autoFocus
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSaveEdit(editValue)
          }}
          className='flex-1 min-w-[220px] max-w-[340px] px-[11px] py-[7px] border border-P-400 rounded text-[13px] outline-none text-N-800'
        />
        <Button size='sm' onClick={() => onSaveEdit(editValue)}>
          Save
        </Button>
        <Button appearance='dull' size='sm' onClick={onCancelEdit}>
          Cancel
        </Button>
        {editError && (
          <span className='text-R-500 text-[12.5px] w-full'>{editError}</span>
        )}
      </div>
    )
  }

  if (isConfirmingDelete) {
    return (
      <div className='flex items-center gap-2.5 flex-wrap px-3 py-[9px] border-b border-N-100 bg-R-50 rounded-md'>
        <span className='text-[13px] text-R-600 font-semibold flex-1 min-w-[220px]'>
          Remove {recipient.recipientEmail} from this report?
        </span>
        <Button appearance='danger' size='sm' onClick={onConfirmDelete}>
          Remove
        </Button>
        <Button appearance='dull' size='sm' onClick={onCancelDelete}>
          Cancel
        </Button>
      </div>
    )
  }

  return (
    <div
      className={`grid ${GRID_COLS} gap-2.5 items-center px-3 py-[9px] border-b border-N-100`}>
      <span
        className='text-[13.5px] text-N-800 truncate'
        title={recipient.recipientEmail}>
        {recipient.recipientEmail}
        {!recipient.isActive && (
          <span className='ml-2 text-[11px] text-N-500'>(inactive)</span>
        )}
      </span>
      <span className='text-[13px] text-N-500 truncate'>
        {recipient.modifiedBy ?? recipient.createdBy ?? '—'}
      </span>
      <span className='text-[13px] text-N-500'>
        {recipient.updatedOn ? dateToReadableString(recipient.updatedOn) : '—'}
      </span>
      <span className='flex justify-end gap-1'>
        {canEdit ? (
          <>
            <button
              type='button'
              title='Edit recipient'
              onClick={onStartEdit}
              className='w-7 h-7 flex items-center justify-center text-N-400 hover:text-P-500 hover:bg-P-50 rounded'>
              <i className='ri-pencil-line text-[14px]'></i>
            </button>
            <button
              type='button'
              title='Remove recipient'
              onClick={onStartDelete}
              className='w-7 h-7 flex items-center justify-center text-N-400 hover:text-R-600 hover:bg-R-100 rounded'>
              <i className='ri-delete-bin-6-line text-[14px]'></i>
            </button>
          </>
        ) : (
          <span className='text-N-300 text-xs'>—</span>
        )}
      </span>
    </div>
  )
}

export default RecipientRow
